import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Reveal } from "../components/Reveal";
import { usePageMeta } from "../lib/api";
import { useEffect } from "react";

const FAQS = [
  { q: "What is supplemental insurance?", a: "Supplemental insurance is coverage designed to work alongside your major medical insurance. Depending on the policy and covered event, it may pay cash benefits that can help with expenses health insurance doesn't touch — like household bills, travel to treatment, or lost income." },
  { q: "How is supplemental insurance different from health insurance?", a: "Health insurance generally pays doctors, hospitals, and providers for covered medical services. Supplemental insurance typically pays benefits to you, and you decide how to use the money. One pays for care; the other can help with everything around the care." },
  { q: "What does accident insurance do?", a: "Accident insurance is designed to provide benefits after a covered accidental injury — things like ER visits, fractures, or hospital stays. The specifics depend entirely on the policy, which is exactly what we'll walk through together." },
  { q: "What is critical illness coverage?", a: "Critical illness coverage is designed around certain covered serious diagnoses — such as cancer, heart attack, or stroke, depending on the policy. It may pay a benefit after a covered diagnosis that you can use however your family needs." },
  { q: "Can supplemental insurance pay cash benefits?", a: "Yes — many supplemental policies pay cash benefits directly to you rather than to a provider, depending on the policy and the covered event. How much, when, and for what varies by policy. We'll show you exactly how specific options work before you decide anything." },
  { q: "What is hospital indemnity insurance?", a: "Hospital indemnity coverage may provide defined benefits related to covered hospital events — for example, a set amount per day or per admission, depending on the policy. It's designed to help with the costs a hospital stay creates beyond the medical bill itself." },
  { q: "Do I need supplemental insurance if I already have health insurance?", a: "Maybe, maybe not — it depends on your deductibles, your savings, who depends on your income, and how a 60-day interruption would hit your household. That's a conversation, not a sales pitch. We'll look at your actual situation together." },
  { q: "How does life insurance work?", a: "Life insurance pays a benefit to the people you choose if you pass away while covered. Term life covers a set period and is usually the most affordable; permanent life lasts a lifetime where offered. The right structure depends on who depends on you and for how long." },
  { q: "How much life insurance should I consider?", a: "A common starting point is to think about income replacement, debts like a mortgage, childcare, and final expenses. There's no universal number — we'll help you work through yours in plain language." },
  { q: "Does Show Me Shield represent one insurance company or multiple options?", a: "Show Me Shield is an independent insurance brokerage. We work with multiple carriers and help you compare options, rather than representing a single company's products." },
  { q: "Is Show Me Shield a government program?", a: "No. Show Me Shield Benefits LLC is a private independent insurance brokerage and is not affiliated with, endorsed by, or operated by the State of Missouri or any state or federal government agency or program." },
  { q: "Does talking with an agent obligate me to buy insurance?", a: "Absolutely not. A conversation is a conversation. We'll explain your options, answer your questions, and the decision is always yours. No pressure — that's the whole point." },
  { q: "What information should I have ready?", a: "Just the basics: who you're looking to protect, what coverage you currently have (if any), and what's on your mind. Never send detailed medical information through a website form — if anything sensitive is needed, we'll handle it properly during the process." },
  { q: "Do you serve all of Missouri?", a: "Yes — we work with individuals and families across Missouri, from Springfield, St. Louis, and Kansas City to Columbia, the Ozarks, and rural communities." },
];

const Faq = () => {
  usePageMeta(
    "Insurance FAQ | Show Me Shield Missouri",
    "Plain-language answers about supplemental insurance, accident and critical illness coverage, hospital indemnity, life insurance, and how Show Me Shield works with Missouri families.",
    { path: "/faq" }
  );

  useEffect(() => {
    const schema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    };
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.id = "faq-schema";
    s.text = JSON.stringify(schema);
    document.head.appendChild(s);
    return () => document.getElementById("faq-schema")?.remove();
  }, []);

  return (
    <main className="bg-paper">
      <div className="border-b border-ink/10 bg-ink grid-lines">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20">
          <Reveal>
            <p className="eyebrow mb-4">Real Answers</p>
            <h1 className="h-display text-4xl text-white sm:text-5xl">
              Insurance shouldn&apos;t require an insurance dictionary.
            </h1>
            <p className="mt-5 max-w-2xl text-base text-zinc-400 sm:text-lg">
              Straight answers to the questions we hear most. Don&apos;t see yours? Call us — that&apos;s
              what we&apos;re here for.
            </p>
          </Reveal>
        </div>
      </div>

      <div className="mx-auto max-w-4xl px-4 py-16 sm:px-6 sm:py-20">
        <Reveal>
          <Accordion type="single" collapsible data-testid="faq-accordion-container" className="w-full">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-ink/10">
                <AccordionTrigger
                  data-testid={`faq-question-${i}`}
                  className="text-left font-display text-lg font-semibold uppercase tracking-wide text-ink hover:text-lime-dim hover:no-underline"
                >
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-base leading-relaxed text-primer">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
        <Reveal className="mt-12 text-center">
          <p className="text-base text-primer">Still wondering about something?</p>
          <Link to="/contact" data-testid="faq-cta" className="btn-lime mt-5">
            Ask Us Directly <ArrowRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </div>
    </main>
  );
};

export default Faq;
